import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_CONTENT_LOCALE,
  useTheme,
  type Course,
  type CourseLessonSummary,
  type PublishState,
} from '@course-authoring/shared-types';
import {
  lessonUsageKeys,
  useCourse,
  useCreateCourse,
  useLessonLibrary,
  useUpdateCourse,
  type LessonListResponseItem,
} from '../../api/courseRecordApi';
import { useCourseClientScope } from '../../hooks/useCourseClientScope';
import { generateTempBlockId } from '../../utils/blockFactory';
import { applyCourseLessonIdMappings, applyIdMappingsToSnapshot } from '../../utils/applyCourseLessonIdMappings';
import {
  ImageInsertModal,
  PublishEditingBanner,
  PublishLockBanner,
  type PublishClusterActions,
  type ImageInsertData,
} from '../../components';
import { useClientContext } from '../../hooks/useClientContext';
import { getRememberedListPath } from '../../hooks/useListPathMemory';
import { useAutosave } from '../../hooks';
import { CourseLearnerPreview, courseLearnerPreviewKeys } from '../CourseLearnerPreviewPage';
import { createCourseEditorPageStyles } from './courseEditorPageStyles';
import { AddLessonModal } from '../../components/AddLessonModal';
import { TranslationLanguageBanner } from '../../components/TranslationStatusPrimitives';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { ResolvedThemeProvider } from '../../theme/ResolvedThemeProvider';
import { ResolvedAccountConfigProvider } from '../../config/ResolvedAccountConfigProvider';
import type { CourseDraft, CourseLessonRowItem } from './types';
import { CourseMetadataSection } from './courseMetadata/CourseMetadataSection';
import { InternalDetailsSection } from './InternalDetailsSection';
import { AuditTrailSection } from './AuditTrailSection';
import { LessonsInCourseSection } from './LessonsInCourseSection';
import { useCourseMetadataEditor } from './useCourseMetadataEditor';
import { CourseEditorHeader } from './CourseEditorHeader';

type CourseEditorView = 'edit' | 'preview';

function normalizeCourse(course: Course, isClientContext: boolean): CourseDraft {
  return {
    id: course.id,
    numericId: course.numericId,
    title: course.title || 'Untitled Course',
    description: course.description || '',
    lessonIds: Array.isArray(course.lessonIds) ? course.lessonIds : [],
    courseLessons: isClientContext && Array.isArray(course.lessons)
      ? course.lessons.map((lesson) => ({
          id: lesson.courseLessonId ?? lesson.id,
          lessonId: lesson.id,
          ...(lesson.clientAdded ? { clientAdded: true } : {}),
          ...(lesson.customizationState ? { customizationState: lesson.customizationState } : {}),
        }))
      : null,
    workflowState: course.workflowState ?? 'draft',
    publishState: course.publishState ?? null,
    deleteEligibility: course.deleteEligibility,
    timedCourse: Boolean(course.timedCourse),
    durationMinutes: typeof course.durationMinutes === 'number' ? course.durationMinutes : null,
    minimumRequiredMinutes: typeof course.minimumRequiredMinutes === 'number' ? course.minimumRequiredMinutes : null,
    thumbnailSrc: course.thumbnailSrc || '',
    thumbnailAlt: course.thumbnailAlt || '',
    completionCertificateEnabled: course.completionCertificateEnabled !== false,
    certificateMessage: course.certificateMessage || '',
    completedMessage: course.completedMessage || '',
    assignedAccountIds: course.assignedAccountIds ?? [],
    customizedByAccountCount: typeof course.customizedByAccountCount === 'number' ? course.customizedByAccountCount : 0,
    createdByUserId: course.createdByUserId ?? null,
    createdByName: course.createdByName || '',
    updatedByUserId: course.updatedByUserId ?? null,
    updatedByName: course.updatedByName || '',
    createdAt: course.createdAt,
    updatedAt: course.updatedAt,
  };
}

function getCourseUpdateSnapshot(draft: CourseDraft) {
  const metadata = {
    timedCourse: draft.timedCourse,
    durationMinutes: draft.durationMinutes,
    minimumRequiredMinutes: draft.minimumRequiredMinutes,
    thumbnailSrc: draft.thumbnailSrc,
    thumbnailAlt: draft.thumbnailAlt,
    completionCertificateEnabled: draft.completionCertificateEnabled,
  };
  // Client-context saves carry the account-resolved composition entries; the
  // base composition (lessonIds) is a Standard-save concern only.
  return draft.courseLessons !== null
    ? { ...metadata, courseLessons: draft.courseLessons }
    : { ...metadata, lessonIds: draft.lessonIds };
}

function toNumberOrNull(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

export function CourseEditorPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tokens } = useTheme();
  const styles = useMemo(() => createCourseEditorPageStyles(tokens), [tokens]);
  const isCreateMode = !courseId;
  const didCreateRef = useRef(false);
  const seededCourseIdRef = useRef<string | null>(null);

  const [draft, setDraft] = useState<CourseDraft | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [addLessonInsertIndex, setAddLessonInsertIndex] = useState<number | null>(null);
  const [lessonSearchQuery, setLessonSearchQuery] = useState('');
  const [debouncedLessonSearch, setDebouncedLessonSearch] = useState('');
  const [isOpeningPreview, setIsOpeningPreview] = useState(false);
  const [currentEditorView, setCurrentEditorView] = useState<CourseEditorView>('edit');
  const {
    selectedLocale,
    setSelectedLocale,
    selectedLanguageLabel,
    registerLocaleChangeGuard,
  } = useLanguageContext();
  const isTranslationMode = selectedLocale !== DEFAULT_CONTENT_LOCALE;
  const {
    generalWriteAccountId,
    isGeneralWriteClientContext,
    metadataAccountId,
    previewAccountId,
    hydrationScopeKey,
  } = useCourseClientScope(courseId, selectedLocale);
  const {
    clientContext: activeClientAccount,
    registerClientContextChangeGuard,
  } = useClientContext();
  const clusterActionsRef = useRef<PublishClusterActions | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedLessonSearch(lessonSearchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [lessonSearchQuery]);

  const { data: course, isLoading: isLoadingCourse, error: courseError } = useCourse(courseId ?? '', {
    enabled: Boolean(courseId),
    locale: selectedLocale,
    accountId: generalWriteAccountId,
  });
  const {
    data: lessonLibraryData,
    isLoading: isLoadingLessons,
    isFetching: isFetchingLessons,
    refetch: refetchLessonLibrary,
    fetchNextPage: fetchNextLessonLibraryPage,
    hasNextPage: hasMoreLessonLibraryPages,
    isFetchingNextPage: isFetchingNextLessonLibraryPage,
    // Client-context adds are limited to lessons assigned to the account (the
    // server enforces the same rule); Standard browsing stays unscoped.
  } = useLessonLibrary(debouncedLessonSearch, generalWriteAccountId);

  const lessonLibrary = useMemo<LessonListResponseItem[]>(
    () => lessonLibraryData?.pages.flatMap((p) => p.items) ?? [],
    [lessonLibraryData]
  );

  // Deliberately not destructuring the mutation's `isPending`: this page
  // navigates from /courses/new to /courses/:courseId, and both routes render
  // the SAME element at the same tree position, so React reuses this component
  // instance instead of remounting. Under StrictMode the simulated remount
  // detaches the mutation observer from the in-flight mutation (MutationObserver
  // drops itself on unsubscribe and never re-attaches), so `isPending` can stay
  // true forever and strand the page on "Creating course...". The route is the
  // authoritative signal for whether the course exists yet.
  const { mutate: createCourse } = useCreateCourse({
    onSuccess: (createdCourse) => {
      navigate(`/courses/${createdCourse.id}`, { replace: true });
    },
    onError: () => setSaveStatus('error'),
  });

  const { mutate: updateCourse, mutateAsync: updateCourseAsync, isPending: isSaving } = useUpdateCourse({
    locale: selectedLocale,
    accountId: generalWriteAccountId,
    onSuccess: (savedCourse) => {
      markSaveSucceeded();
      const idMappings = savedCourse.idMappings ?? {};
      const hasIdMappings = Object.keys(idMappings).length > 0;
      if (hasIdMappings) {
        // A client-context save created anchors for client-added lessons: swap
        // the draft's tmp ids for the server ids, and rewrite the autosave
        // baselines to match, or the next save would re-submit those lessons
        // as new adds (rejected as lessonAlreadyInCourse).
        lastSavedSnapshotRef.current = applyIdMappingsToSnapshot(lastSavedSnapshotRef.current, idMappings);
        pendingSaveSnapshotRef.current = applyIdMappingsToSnapshot(pendingSaveSnapshotRef.current, idMappings);
      }
      setDraft((current) => current
        ? {
            ...current,
            ...(hasIdMappings && current.courseLessons
              ? { courseLessons: applyCourseLessonIdMappings(current.courseLessons, idMappings) }
              : {}),
            updatedAt: savedCourse.updatedAt,
            updatedByUserId: savedCourse.updatedByUserId ?? null,
            updatedByName: savedCourse.updatedByName || current.updatedByName,
          }
        : current);
    },
    onError: () => setSaveStatus('error'),
  });

  const {
    status: saveStatus,
    setStatus: setSaveStatus,
    lastSavedSnapshotRef,
    pendingSaveSnapshotRef,
    flushIfDirty,
    markSaveSucceeded,
  } = useAutosave({
    // getSnapshot/save only run from the interval, which is gated on `draft`
    // being non-null via `enabled`.
    enabled: Boolean(draft && courseId),
    getSnapshot: () => getCourseUpdateSnapshot(draft!),
    save: (input) => updateCourse({ id: draft!.id, input }),
    isSaving,
    minSavingMs: 600,
    savedDisplayMs: 1600,
  });

  useEffect(() => {
    if (!isCreateMode || didCreateRef.current) return;

    didCreateRef.current = true;
    setSaveStatus('saving');
    createCourse({ title: 'Untitled Course' });
  }, [createCourse, isCreateMode]);

  useEffect(() => {
    if (!course) return;
    if (course.locale && course.locale !== selectedLocale) return;
    // Seed the draft only once per hydration scope (course + locale + client
    // account). The update mutation writes the server payload back into the
    // query cache on every autosave, and re-seeding from it here would clobber
    // anything typed while the PUT was in flight. Post-save server fields are
    // merged in the mutation's onSuccess instead. Switching client context
    // changes the scope key, so the draft reseeds and the autosave baseline
    // resets (no cross-context dirty save).
    const seedKey = hydrationScopeKey;
    if (seededCourseIdRef.current === seedKey) return;
    seededCourseIdRef.current = seedKey;

    const nextDraft = normalizeCourse(course, isGeneralWriteClientContext);
    setDraft(nextDraft);
    lastSavedSnapshotRef.current = JSON.stringify(getCourseUpdateSnapshot(nextDraft));
    pendingSaveSnapshotRef.current = lastSavedSnapshotRef.current;
    setSaveStatus('idle');
  }, [course, hydrationScopeKey, isGeneralWriteClientContext, pendingSaveSnapshotRef, lastSavedSnapshotRef, selectedLocale, setSaveStatus]);

  // publishState/workflowState/deleteEligibility are server-owned fields — the
  // author never types them, so folding them in from every refetch is
  // clobber-safe. The seed-once guard above protects typed content; without
  // this sync a lifecycle transition (publish, unlock, publish update) left the
  // editor rendering the stale locked/draft UI until a full page reload, and
  // (NA-283) the top-bar Delete rendering pre-transition eligibility. Autosave
  // writebacks may omit these fields from the cached course — skip those.
  useEffect(() => {
    if (!course || course.publishState === undefined) return;
    setDraft((current) => {
      if (!current || current.id !== course.id) return current;
      const nextPublishState = course.publishState ?? null;
      const nextWorkflowState = course.workflowState ?? current.workflowState;
      const nextDeleteEligibility = course.deleteEligibility ?? current.deleteEligibility;
      const unchanged =
        current.workflowState === nextWorkflowState &&
        JSON.stringify(current.publishState) === JSON.stringify(nextPublishState) &&
        JSON.stringify(current.deleteEligibility) === JSON.stringify(nextDeleteEligibility);
      return unchanged
        ? current
        : {
          ...current,
          publishState: nextPublishState,
          workflowState: nextWorkflowState,
          deleteEligibility: nextDeleteEligibility,
        };
    });
  }, [course]);

  const lessonSummaryById = useMemo(() => {
    const map = new Map<string, CourseLessonSummary>();
    const summaries = course?.lessons;
    if (summaries) {
      for (const s of summaries) map.set(s.id, s);
    }
    return map;
  }, [course]);

  const lessonById = useMemo(() => {
    const map = new Map<string, LessonListResponseItem>();
    for (const lesson of lessonLibrary) map.set(lesson.id, lesson);
    return map;
  }, [lessonLibrary]);

  const selectedLessons = useMemo<CourseLessonRowItem[]>(() => {
    if (!draft) return [];

    const toRow = (lessonId: string, entryId: string, clientAdded: boolean, suppressed: boolean): CourseLessonRowItem => {
      const summary = lessonSummaryById.get(lessonId);
      const libraryLesson = lessonById.get(lessonId);
      return {
        id: lessonId,
        numericId: summary?.numericId ?? libraryLesson?.numericId,
        entryId,
        clientAdded,
        suppressed,
        title: summary?.title || libraryLesson?.title || 'Untitled Lesson',
        workflowState: summary?.workflowState ?? libraryLesson?.workflowState ?? 'draft',
        publishLifecycle: summary?.publishLifecycle ?? libraryLesson?.publishLifecycle,
        estimatedTimeMinutes: summary?.estimatedTimeMinutes ?? libraryLesson?.estimatedTimeMinutes,
      };
    };

    if (draft.courseLessons !== null) {
      return draft.courseLessons.map((entry) =>
        toRow(entry.lessonId, entry.id, Boolean(entry.clientAdded), entry.customizationState === 'suppressed'));
    }
    return draft.lessonIds.map((lessonId) => toRow(lessonId, lessonId, false, false));
  }, [draft, lessonSummaryById, lessonById]);

  const totalEstimatedMinutes = useMemo(
    () => selectedLessons
      .filter((lesson) => !lesson.suppressed)
      .reduce((total, lesson) => total + (lesson.estimatedTimeMinutes ?? 0), 0),
    [selectedLessons]
  );

  const selectedLessonIds = useMemo(
    () => new Set(selectedLessons.map((lesson) => lesson.id)),
    [selectedLessons]
  );

  const updateDraft = useCallback((updates: Partial<CourseDraft>) => {
    setDraft((current) => current ? { ...current, ...updates } : current);
  }, []);

  const {
    metadataProjection,
    englishMetadataProjection,
    isLoadingMetadata,
    isLoadingEnglishMetadata,
    metadataError,
    englishMetadataError,
    isMetadataSaving,
    metadataAutosave,
    replacementView,
    updateMetadata,
    handleReplacementDecision,
    handleChangeReplacementDecision,
  } = useCourseMetadataEditor({
    courseId,
    selectedLocale,
    metadataAccountId,
    isTranslationMode,
    updateCourseDraft: updateDraft,
  });

  const handleDurationChange = useCallback((value: string) => {
    updateDraft({ durationMinutes: toNumberOrNull(value) });
  }, [updateDraft]);

  const handleMinimumRequiredChange = useCallback((value: string) => {
    updateDraft({ minimumRequiredMinutes: toNumberOrNull(value) });
  }, [updateDraft]);

  const handleImageInsert = useCallback((image: ImageInsertData) => {
    updateDraft({ thumbnailSrc: image.src, thumbnailAlt: image.src ? image.alt : '' });
    setIsImageModalOpen(false);
  }, [updateDraft]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!draft || !over || active.id === over.id) return;

    if (draft.courseLessons !== null) {
      const oldIndex = draft.courseLessons.findIndex((entry) => entry.id === String(active.id));
      const newIndex = draft.courseLessons.findIndex((entry) => entry.id === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      // Only client-added rows can be picked up (base rows' sortables are
      // disabled), so a drop past base rows leaves base order intact.
      if (!draft.courseLessons[oldIndex]?.clientAdded) return;
      updateDraft({ courseLessons: arrayMove(draft.courseLessons, oldIndex, newIndex) });
      return;
    }

    const oldIndex = draft.lessonIds.indexOf(String(active.id));
    const newIndex = draft.lessonIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    updateDraft({ lessonIds: arrayMove(draft.lessonIds, oldIndex, newIndex) });
  }, [draft, updateDraft]);

  const handleOpenAddLessonModal = useCallback((insertIndex: number) => {
    void refetchLessonLibrary();
    setAddLessonInsertIndex(insertIndex);
    setLessonSearchQuery('');
  }, [refetchLessonLibrary]);

  const handleAddLesson = useCallback((lessonId: string) => {
    if (!draft || addLessonInsertIndex === null || selectedLessonIds.has(lessonId)) return;

    if (draft.courseLessons !== null) {
      // Client-added lessons get a tmp id until the save maps it to the new
      // account-owned course-lesson anchor.
      const nextEntries = [...draft.courseLessons];
      const targetIndex = Math.min(addLessonInsertIndex, nextEntries.length);
      nextEntries.splice(targetIndex, 0, { id: generateTempBlockId(), lessonId, clientAdded: true });
      updateDraft({ courseLessons: nextEntries });
      setAddLessonInsertIndex(targetIndex + 1);
      return;
    }

    const nextLessonIds = [...draft.lessonIds];
    const targetIndex = Math.min(addLessonInsertIndex, nextLessonIds.length);
    nextLessonIds.splice(targetIndex, 0, lessonId);
    updateDraft({ lessonIds: nextLessonIds });
    setAddLessonInsertIndex(targetIndex + 1);
  }, [addLessonInsertIndex, draft, selectedLessonIds, updateDraft]);

  // Confirmation is handled inline by the row's two-click delete button (the
  // lesson editor pattern), so this applies the action directly: hide for base
  // lessons in client context, true delete for client-added lessons, removal
  // from the base composition in Standard context.
  const handleRemoveLesson = useCallback((entryId: string) => {
    if (!draft) return;

    if (draft.courseLessons !== null) {
      const entry = draft.courseLessons.find((candidate) => candidate.id === entryId);
      if (!entry) return;
      updateDraft({
        courseLessons: entry.clientAdded
          ? draft.courseLessons.filter((candidate) => candidate.id !== entryId)
          : draft.courseLessons.map((candidate) => (candidate.id === entryId
            ? { ...candidate, customizationState: 'suppressed' as const }
            : candidate)),
      });
      return;
    }

    updateDraft({ lessonIds: draft.lessonIds.filter((lessonId) => lessonId !== entryId) });
  }, [draft, updateDraft]);

  const handleReEnableLesson = useCallback((entryId: string) => {
    if (!draft || draft.courseLessons === null) return;
    updateDraft({
      courseLessons: draft.courseLessons.map((candidate) => (candidate.id === entryId
        ? { ...candidate, customizationState: 'inherited' as const }
        : candidate)),
    });
  }, [draft, updateDraft]);

  const handleOpenLesson = useCallback((lessonId: string) => {
    window.open(`/lessons/${lessonId}`, '_blank', 'noopener,noreferrer');
  }, []);

  // NA-207: Published — locked is read-only in the active context.
  const isEditorLocked = isTranslationMode
    ? metadataProjection?.editable === false
    : draft?.publishState != null && draft.publishState.editable === false;

  const handlePublishStateChange = useCallback((state: PublishState | null) => {
    // Apply a known post-transition state immediately (unlock and publish
    // update hand back the new state); a null state means "unknown — resync",
    // which the refetch below satisfies via the lifecycle-sync effect.
    if (state) {
      setDraft((current) => (current ? { ...current, publishState: state } : current));
    }
    // Lifecycle transitions change serialized reads everywhere — refetch.
    void queryClient.invalidateQueries({ queryKey: ['courseRecords'] });
    void queryClient.invalidateQueries({ queryKey: ['lessons'] });
    // NA-284: usage rows show this course's lifecycle badge. A base
    // transition is known-changed data — drop cached usage so the panel's
    // next open loads fresh instead of flashing the old badge. Client
    // transitions touch account publish states, which base-scoped usage
    // ignores.
    if (activeClientAccount === null) {
      queryClient.removeQueries({ queryKey: lessonUsageKeys.all });
    }
  }, [queryClient, activeClientAccount]);

  const handleCreateLesson = useCallback(() => {
    window.open('/lessons/new', '_blank', 'noopener,noreferrer');
  }, []);

  const handlePreviewCourse = useCallback(async () => {
    if (!draft || isOpeningPreview) return;

    setIsOpeningPreview(true);

    try {
      await metadataAutosave.flushIfDirty();
      await flushIfDirty((snapshot) => updateCourseAsync({ id: draft.id, input: snapshot }));

      queryClient.removeQueries({
        queryKey: courseLearnerPreviewKeys.detail(draft.id, selectedLocale, previewAccountId),
      });
      setCurrentEditorView('preview');
    } catch {
      setSaveStatus('error');
    } finally {
      setIsOpeningPreview(false);
    }
  }, [draft, flushIfDirty, isOpeningPreview, metadataAutosave.flushIfDirty, previewAccountId, queryClient, selectedLocale, setSaveStatus, updateCourseAsync]);

  const handleEditCourse = useCallback(() => {
    setCurrentEditorView('edit');
  }, []);

  // Persist any unsaved course edits before the export package is built so the
  // loader sees the same saved state as Course Preview. Passed to
  // the publish action cluster, which owns the publish + deploy flow.
  const persistDraftBeforeExport = useCallback(async () => {
    if (!draft) {
      throw new Error('No course loaded');
    }
    await metadataAutosave.flushIfDirty();
    await flushIfDirty((snapshot) => updateCourseAsync({ id: draft.id, input: snapshot }));
  }, [draft, flushIfDirty, metadataAutosave.flushIfDirty, updateCourseAsync]);

  const guardScopeTransition = useCallback(async () => {
    if (!draft) return true;
    try {
      await metadataAutosave.flushIfDirty();
      await flushIfDirty((snapshot) => updateCourseAsync({ id: draft.id, input: snapshot }));
      return true;
    } catch {
      setSaveStatus('error');
      return false;
    }
  }, [draft, flushIfDirty, metadataAutosave.flushIfDirty, setSaveStatus, updateCourseAsync]);

  useEffect(
    () => registerLocaleChangeGuard?.(guardScopeTransition),
    [guardScopeTransition, registerLocaleChangeGuard],
  );

  useEffect(
    () => registerClientContextChangeGuard?.(guardScopeTransition),
    [guardScopeTransition, registerClientContextChangeGuard],
  );

  const handlePreviewLocaleChange = useCallback(async (locale: string) => {
    if (!draft) return;
    try {
      await metadataAutosave.flushIfDirty();
      await flushIfDirty((snapshot) => updateCourseAsync({ id: draft.id, input: snapshot }));
      setSelectedLocale(locale);
    } catch {
      // Keep the current locale active when either save owner cannot flush.
    }
  }, [draft, flushIfDirty, metadataAutosave.flushIfDirty, setSelectedLocale, updateCourseAsync]);

  const saveStatusText = isCreateMode
    ? 'Creating course...'
    : saveStatus === 'saving' || metadataAutosave.status === 'saving'
      ? 'Saving...'
      : saveStatus === 'error' || metadataAutosave.status === 'error'
        ? 'Unable to save'
        : 'All changes saved';

  if (isCreateMode) {
    return (
      <div style={{ ...styles.page, ...(currentEditorView === 'preview' ? styles.pagePreview : {}) }}>
        <div style={styles.emptyState}>Creating course...</div>
      </div>
    );
  }

  if (courseError || metadataError || englishMetadataError) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyState}>Unable to load this course.</div>
      </div>
    );
  }

  if (
    isLoadingCourse
    || isLoadingMetadata
    || (isTranslationMode && isLoadingEnglishMetadata)
    || !draft
    || !metadataProjection
    || (isTranslationMode && !englishMetadataProjection)
  ) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyState}>Loading course...</div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.page}>
        <CourseEditorHeader
          styles={styles}
          draft={draft}
          titleValue={isTranslationMode
            ? englishMetadataProjection?.title ?? ''
            : metadataAutosave.draft.title}
          publishTitle={metadataAutosave.draft.title}
          isTranslationMode={isTranslationMode}
          isEditorLocked={isEditorLocked}
          currentEditorView={currentEditorView}
          saveStatusText={saveStatusText}
          hasSaveError={saveStatus === 'error' || metadataAutosave.status === 'error'}
          isCreating={isCreateMode}
          isOpeningPreview={isOpeningPreview}
          deleteEligibility={draft.deleteEligibility}
          isStandardContext={activeClientAccount === null}
          clientContext={isGeneralWriteClientContext && generalWriteAccountId !== null ? {
            accountId: generalWriteAccountId,
            accountName: activeClientAccount?.name ?? '',
            ...(activeClientAccount?.subdomain ? { accountSlug: activeClientAccount.subdomain } : {}),
          } : null}
          lessonTitlesById={Object.fromEntries(
            selectedLessons.map((lesson) => [lesson.id, lesson.title]),
          )}
          onBack={() => navigate(getRememberedListPath('/courses'))}
          onTitleChange={(title) => updateMetadata({ title })}
          onTogglePreview={currentEditorView === 'preview' ? handleEditCourse : handlePreviewCourse}
          onBeforePublishAction={persistDraftBeforeExport}
          onNavigateToLesson={handleOpenLesson}
          onPublishStateChange={handlePublishStateChange}
          onRegisterPublishActions={(actions) => { clusterActionsRef.current = actions; }}
        />

        {currentEditorView === 'preview' ? (
          <main style={styles.previewPane}>
            <ResolvedThemeProvider>
              <ResolvedAccountConfigProvider>
                <CourseLearnerPreview
                  courseId={draft.id}
                  variant="embedded"
                  selectedLocale={selectedLocale}
                  onLocaleChange={(locale) => { void handlePreviewLocaleChange(locale); }}
                  accountId={previewAccountId}
                />
              </ResolvedAccountConfigProvider>
            </ResolvedThemeProvider>
          </main>
        ) : (
        <>
          {isTranslationMode && (
            <TranslationLanguageBanner language={selectedLanguageLabel} flush />
          )}
          {!isTranslationMode && draft.publishState !== null && draft.publishState.state !== 'draft' && (
            <div style={{ padding: '16px 24px 0' }}>
              {draft.publishState.state === 'locked' ? (
                <PublishLockBanner
                  contentKind="course"
                  lockedByEmtrainEditing={draft.publishState.lockedByEmtrainEditing}
                  lastPublishedAt={draft.publishState.lastPublishedAt}
                  onUnlock={draft.publishState.lockedByEmtrainEditing
                    ? undefined
                    : () => clusterActionsRef.current?.openUnlock()}
                  packageId={!isGeneralWriteClientContext || activeClientAccount?.subdomain ? courseId : undefined}
                  accountSlug={isGeneralWriteClientContext ? activeClientAccount?.subdomain : undefined}
                />
              ) : (
                <PublishEditingBanner
                  onPublishUpdate={() => clusterActionsRef.current?.openPublishUpdate()}
                  onCancelEdit={() => clusterActionsRef.current?.cancelEdit()}
                  canCancelEdit={draft.publishState.canCancelEdit}
                />
              )}
            </div>
          )}
        <main
          style={{
            ...styles.content,
            // Published — locked: fields read-only, controls inert, content
            // dimmed but fully readable (NA-207); the server enforces the gate.
            ...(isEditorLocked ? { opacity: 0.6, pointerEvents: 'none' as const } : {}),
          }}
          aria-disabled={isEditorLocked || undefined}
          inert={isEditorLocked ? true : undefined}
        >
          <div style={styles.leftColumn}>
            <CourseMetadataSection
              draft={draft}
              metadata={metadataAutosave.draft}
              mode={isTranslationMode ? 'translation' : 'english'}
              translationStatus={metadataProjection.status ?? undefined}
              editabilityReason={metadataProjection.editabilityReason}
              humanTranslationCount={metadataProjection.summary.humanTranslationCount}
              replacementView={replacementView}
              isSubmitting={isMetadataSaving}
              disabled={isEditorLocked}
              styles={styles}
              onUpdateDraft={updateDraft}
              onUpdateMetadata={updateMetadata}
              onQueueTranslations={() => { void handleReplacementDecision('queue'); }}
              onPreserveTranslations={() => { void handleReplacementDecision('preserve'); }}
              onChangeReplacementDecision={handleChangeReplacementDecision}
              onDurationChange={handleDurationChange}
              onMinimumRequiredChange={handleMinimumRequiredChange}
              onOpenImageModal={() => setIsImageModalOpen(true)}
            />

            {!isTranslationMode && <InternalDetailsSection draft={draft} styles={styles} />}

            {!isTranslationMode && <AuditTrailSection draft={draft} styles={styles} />}
          </div>

          <div
            aria-disabled={isTranslationMode || undefined}
            inert={isTranslationMode ? true : undefined}
            style={isTranslationMode
              ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none' }
              : undefined}
          >
            <LessonsInCourseSection
              styles={styles}
              selectedLessons={selectedLessons}
              totalEstimatedMinutes={totalEstimatedMinutes}
              isClientContext={isGeneralWriteClientContext}
              // Derived from the LIVE composition: in client context edits mutate
              // courseLessons (lessonIds stays at its seeded value), so using
              // lessonIds.length here would splice "add at end" before any
              // lessons added since the seed.
              addLessonEndIndex={selectedLessons.length}
              onOpenAddLessonModal={handleOpenAddLessonModal}
              onDragEnd={handleDragEnd}
              onOpenLesson={handleOpenLesson}
              onRemoveLesson={handleRemoveLesson}
              onReEnableLesson={handleReEnableLesson}
            />
          </div>
        </main>
        </>
        )}
      </div>

      <ImageInsertModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        onInsert={handleImageInsert}
        targetAspectRatio="16 / 9"
        initialImage={draft.thumbnailSrc ? { src: draft.thumbnailSrc, alt: draft.thumbnailAlt } : undefined}
      />

      {addLessonInsertIndex !== null && (
        <AddLessonModal
          styles={styles}
          lessons={lessonLibrary}
          selectedLessonIds={selectedLessonIds}
          resultCount={lessonLibraryData?.pages.at(-1)?.totalCount ?? lessonLibrary.length}
          searchValue={lessonSearchQuery}
          onSearchChange={setLessonSearchQuery}
          isLoading={isLoadingLessons}
          isRefreshing={isFetchingLessons}
          onRefresh={() => void refetchLessonLibrary()}
          hasNextPage={hasMoreLessonLibraryPages}
          isFetchingNextPage={isFetchingNextLessonLibraryPage}
          onFetchNextPage={() => { void fetchNextLessonLibraryPage(); }}
          onAdd={handleAddLesson}
          onClose={() => setAddLessonInsertIndex(null)}
          onCreateLesson={handleCreateLesson}
        />
      )}

    </>
  );
}
